import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'

import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'

import { ETH_ADDR_ZERO, EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'

import LenderPoolHoldings from '@react/lender/LenderPoolHoldings'

import React, { useEffect } from 'react'

import ButtonModal from '@react/components/ButtonModal'
import DynamicCreditDebitForm from '@react/dynamic/DynamicCreditDebitForm'
import { fetchPoolParticipants } from '@react/fetchers/PoolFetchers'
import { companyProfilesGet } from '@react/fetchers/companyProfileFetchers'
import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { logOT } from '@shared/loggingShared'
import { findBlockByTimestamp } from '@shared/sharedUtils'

export const AdminLenderView = (props: { poolAddr: EthAddr }): React.ReactElement => {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()

  const [participants, setParticipants] = React.useState<PoolAccountAccessPair[]>([])
  const [selectedParticipant, setSelectedParticipant] = React.useState<EthAddr>('')
  const [selectingDate, setSelectingDate] = React.useState<boolean>(false)

  const [companyProfiles, setCompanyProfiles] = React.useState<CompanyProfileCTO[]>([])
  const chainTimestamp = useOtChainTimestamp()

  const [blockNumberSnapshot, setBlockNumberSnapshot] = React.useState<number | undefined>(undefined)
  const [dateValue, setDateValue] = React.useState<Dayjs>(dayjs((chainTimestamp ?? 0) * 1000))
  const { poolAddr } = props
  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null

  useEffect(() => {
    if (chainTimestamp) {
      setDateValue(dayjs(chainTimestamp * 1000))
    }
  }, [chainTimestamp])

  useEffect(() => {
    if (chainTimestamp) {
      setDateValue(dayjs(chainTimestamp * 1000))
    }
  }, [chainTimestamp])

  useEffect(() => {
    if (serverApiBaseUrl) {
      companyProfilesGet(serverApiBaseUrl).then((_companyProfiles) => {
        setCompanyProfiles(_companyProfiles)
      })
    }
  }, [serverApiBaseUrl])

  useEffect(() => {
    if (providerReadOnly && poolAddr && serverApiBaseUrl && restAPIKey) {
      fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_participants) => {
          logOT.debug('all users', _participants)
          setParticipants(_participants)
        })
        .catch((error) => {
          logOT.error(error)
        })
    }
  }, [providerReadOnly, poolAddr])
  const getCompanyName = (userAddr: EthAddr): string => {
    const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(userAddr))

    return companyProfile?.companyName ?? ''
  }

  return (
    <Paper>
      <form>
        <Box px={3} py={2}>
          <Typography variant="bodyBold" align="center" margin="dense">
            View Lenders Account view for pool
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={6}>
              <FormControl sx={{ m: 1, width: '80%' }}>
                <InputLabel id="asset-label">Lender</InputLabel>
                <Select
                  labelId="asset-label"
                  id="asset-select"
                  input={<OutlinedInput id="asset-select" label="Asset" />}
                  value={selectedParticipant}
                  renderValue={(value: string) => {
                    return (
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {getCompanyName(value) + '  '} {value}
                      </Typography>
                    )
                  }}
                  onChange={(event: SelectChangeEvent) => {
                    logOT.info('event.target.value', event.target.value)
                    setSelectedParticipant(event.target.value as string)
                  }}
                >
                  {participants.map((p: PoolAccountAccessPair) => (
                    <MenuItem
                      value={p.accountAddr}
                      key={`asset-option-${p.accountAddr}`}
                      style={{
                        paddingLeft: 12,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ marginLeft: 2 }} variant="bodyBold">
                        {getCompanyName(p.accountAddr) + '  '}
                        {p.accountAddr}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3} sm={3}>
              {poolDynamicOverviewCTO && roleEnum === RoleEnum.POOL_ADMIN && selectedParticipant.length > 0 && (
                <ButtonModal title={'Debit or Credit'} modalWidth={900} variant="primary">
                  <DynamicCreditDebitForm poolAddr={poolAddr} lenderAddr={selectedParticipant} />
                </ButtonModal>
              )}
            </Grid>
            <Grid item xs={3} sm={3}>
              {!selectingDate && (
                <Typography variant="bodyBold" align="center" margin="dense" onClick={() => setSelectingDate(true)}>
                  Date {dateValue.format('YYYY-MM-DD')}
                </Typography>
              )}
              {selectingDate && providerReadOnly && (
                <>
                  <InputLabel id="asset-label">Date {dateValue.format('YYYY-MM-DD')} </InputLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      value={dateValue}
                      onChange={(newValue) => {
                        logOT.info('New Date value', newValue)
                        setSelectingDate(false)

                        setDateValue(newValue)
                        const blockTimeStamp = newValue.unix()
                        findBlockByTimestamp(providerReadOnly, blockTimeStamp)
                          .then((_blockNumber: number) => {
                            logOT.info('blockNumber', blockNumberSnapshot)
                            setBlockNumberSnapshot(_blockNumber)
                          })
                          .catch((error) => {
                            console.error('Error finding block', error)
                          })
                      }}
                    />
                  </LocalizationProvider>
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              {poolAddr && selectedParticipant !== '' && (
                <LenderPoolHoldings
                  poolAddr={poolAddr}
                  accountAddr={selectedParticipant}
                  blockNumberSnapshot={blockNumberSnapshot}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </form>
    </Paper>
  )
}
